import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useCallback, useRef } from 'react';

// ── Tipos públicos ──

export type Criticidade = 'critico' | 'alerta' | 'ok' | 'sem_dados';

export interface GeoClusterPoint {
  id: string;
  name: string;
  color: string;
  coordinates: [number, number]; // [lng, lat]
  criticidade: Criticidade;
  /** contratos dentro do cluster */
  contratos: GeoContratoPoint[];
  total_alertas: number;
  total_desvios: number;
  has_valid_coordinates: boolean;
}

export interface GeoContratoPoint {
  id: string;
  nome: string;
  status: string;
  cluster_id: string;
  cluster_name: string;
  cluster_color: string;
  coordinates: [number, number]; // herda do cluster
  criticidade: Criticidade;
  spi: number | null;
  cpi: number | null;
  percentual_fisico: number | null;
  alertas_abertos: number;
  desvios_abertos: number;
  has_valid_coordinates: boolean;
}

// ── Helpers ──

function calcCriticidade(spi: number | null, alertas: number): Criticidade {
  if (spi === null) return alertas > 0 ? 'alerta' : 'sem_dados';
  if (spi < 0.7 || alertas >= 2) return 'critico';
  if (spi < 0.9 || alertas >= 1) return 'alerta';
  return 'ok';
}

function worstCriticidade(list: Criticidade[]): Criticidade {
  const order: Criticidade[] = ['critico', 'alerta', 'ok', 'sem_dados'];
  for (const c of order) {
    if (list.includes(c)) return c;
  }
  return 'sem_dados';
}

/** Calcula centroide simples (média das coordenadas) */
function calcCentroid(coords: [number, number][]): [number, number] {
  const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return [lng, lat];
}

const BRAZIL_CENTER: [number, number] = [-52, -15];

// ── Fetch principal ──

async function fetchGeoIntel(): Promise<{ clusters: GeoClusterPoint[]; contratos: GeoContratoPoint[] }> {
  // 1. Clusters
  const { data: clustersRaw, error: clErr } = await supabase
    .from('clusters')
    .select('id, name, color, sede_municipio_ibge')
    .order('name');
  if (clErr) throw clErr;

  // 2. Emp_contratos — inclui municipio_ibge para posicionamento preciso por contrato
  const { data: contratosRaw, error: coErr } = await supabase
    .from('emp_contratos')
    .select('id, nome, status, cluster_id, municipio_ibge')
    .order('nome');
  if (coErr) throw coErr;

  // 3. Emp_indicadores - último período por contrato
  const { data: indicadoresRaw } = await supabase
    .from('emp_vw_indicadores_blindados' as any)
    .select('contrato_id, spi, cpi, percentual_fisico_realizado, periodo_referencia')
    .order('periodo_referencia', { ascending: false }) as any;

  // último indicador por contrato
  const indicadorMap = new Map<string, { spi: number | null; cpi: number | null; pct: number | null }>();
  (indicadoresRaw || []).forEach(i => {
    if (!indicadorMap.has(i.contrato_id)) {
      indicadorMap.set(i.contrato_id, {
        spi: i.spi,
        cpi: i.cpi,
        pct: i.percentual_fisico_realizado,
      });
    }
  });

  // 4. Emp_alertas - contagem de ativos por contrato
  const { data: alertasRaw } = await supabase
    .from('emp_alertas')
    .select('contrato_id, severidade')
    .eq('status', 'ativo');

  const alertasMap = new Map<string, number>();
  (alertasRaw || []).forEach(a => {
    alertasMap.set(a.contrato_id, (alertasMap.get(a.contrato_id) || 0) + 1);
  });

  // 5. Emp_desvios - contagem de abertos por contrato
  const { data: desviosRaw } = await supabase
    .from('emp_desvios')
    .select('contrato_id')
    .eq('status', 'aberto');

  const desviosMap = new Map<string, number>();
  (desviosRaw || []).forEach(d => {
    desviosMap.set(d.contrato_id, (desviosMap.get(d.contrato_id) || 0) + 1);
  });

  // 6. Works (para centroide dos clusters sem coordenada de município)
  // IMPORTANTE: works.coordinates é numeric[] no formato [latitude, longitude]
  // Precisamos inverter para [longitude, latitude] que é o padrão MapLibre/GeoJSON
  const { data: worksRaw } = await supabase
    .from('works')
    .select('coordinates, contract_id, contracts!inner(cluster_id)');

  // coordenadas de obras por cluster — convertendo [lat, lng] → [lng, lat]
  const clusterWorksCoords = new Map<string, [number, number][]>();
  (worksRaw || []).forEach((w: any) => {
    const clusterId = w.contracts?.cluster_id;
    if (!clusterId || !w.coordinates) return;
    const coords = Array.isArray(w.coordinates) ? w.coordinates : null;
    if (!coords || coords.length < 2) return;
    // works.coordinates = [latitude, longitude] → inverter para [longitude, latitude]
    const lat = Number(coords[0]);
    const lng = Number(coords[1]);
    if (isNaN(lng) || isNaN(lat)) return;
    // Validação de bounds do Brasil: lng [-74, -28], lat [-34, 6]
    if (lng < -74 || lng > -28 || lat < -34 || lat > 6) {
      console.warn(`[GeoIntel] Coordenada de obra fora do Brasil ignorada: lat=${lat} lng=${lng}`);
      return;
    }
    if (!clusterWorksCoords.has(clusterId)) clusterWorksCoords.set(clusterId, []);
    clusterWorksCoords.get(clusterId)!.push([lng, lat]);
  });

  // 7. municipio_coordenadas
  const { data: munCoords } = await supabase
    .from('municipio_coordenadas')
    .select('codigo_ibge, longitude, latitude');

  const munMap = new Map<string, [number, number]>();
  (munCoords || []).forEach(m => {
    const lng = Number(m.longitude);
    const lat = Number(m.latitude);
    // Validar bounds do Brasil: lng [-74, -28], lat [-34, 6]
    if (!isNaN(lng) && !isNaN(lat) && lng >= -74 && lng <= -28 && lat >= -34 && lat <= 6) {
      munMap.set(m.codigo_ibge, [lng, lat]);
    }
  });

  if (munCoords && munCoords.length === 0) {
    console.warn('[GeoIntel] municipio_coordenadas está vazia — usando fallback Turf.js das obras');
  }

  // ── Resolver coordenada do cluster ──
  function resolveClusterCoords(cluster: { id: string; sede_municipio_ibge?: string | null }): { coords: [number, number]; valid: boolean } {
    // 1. Sede do cluster
    if (cluster.sede_municipio_ibge && munMap.has(cluster.sede_municipio_ibge)) {
      const coords = munMap.get(cluster.sede_municipio_ibge)!;
      console.info(`[GeoIntel] Cluster ${cluster.id}: coordenada via sede ${cluster.sede_municipio_ibge} → [${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}]`);
      return { coords, valid: true };
    }
    // 2. Fallback: centroide das obras do cluster
    const workCoords = clusterWorksCoords.get(cluster.id);
    if (workCoords && workCoords.length > 0) {
      const coords = calcCentroid(workCoords);
      console.info(`[GeoIntel] Fallback Turf.js aplicado para Cluster ${cluster.id} → [${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}]`);
      return { coords, valid: true };
    }
    console.warn(`[GeoIntel] Cluster ${cluster.id}: sem coordenadas válidas — usando centro do Brasil`);
    return { coords: BRAZIL_CENTER, valid: false };
  }

  // ── Montar contratos ──
  const clusterMap = new Map((clustersRaw || []).map(c => [c.id, c]));

  const contratos: GeoContratoPoint[] = (contratosRaw || []).map((ec: any) => {
    const cluster = clusterMap.get(ec.cluster_id);
    const ind = indicadorMap.get(ec.id);
    const alertas = alertasMap.get(ec.id) || 0;
    const desvios = desviosMap.get(ec.id) || 0;
    const crit = calcCriticidade(ind?.spi ?? null, alertas);

    let coords: [number, number] = BRAZIL_CENTER;
    let valid = false;

    // Prioridade 1: municipio_ibge específico do contrato
    if (ec.municipio_ibge && munMap.has(ec.municipio_ibge)) {
      coords = munMap.get(ec.municipio_ibge)!;
      valid = true;
      console.info(`[GeoIntel] Contrato "${ec.nome}" → coords via municipio_ibge [${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}]`);
    } else if (cluster) {
      // Prioridade 2: herda coordenadas do cluster
      const resolved = resolveClusterCoords({ id: cluster.id, sede_municipio_ibge: (cluster as any).sede_municipio_ibge });
      coords = resolved.coords;
      valid = resolved.valid;
      console.info(`[GeoIntel] Contrato "${ec.nome}" → coords via cluster [${coords[0].toFixed(4)}, ${coords[1].toFixed(4)}] valid=${valid}`);
    }

    return {
      id: ec.id,
      nome: ec.nome,
      status: ec.status,
      cluster_id: ec.cluster_id,
      cluster_name: cluster?.name ?? '',
      cluster_color: cluster?.color ?? '#6B7280',
      coordinates: coords,
      criticidade: crit,
      spi: ind?.spi ?? null,
      cpi: ind?.cpi ?? null,
      percentual_fisico: ind?.pct ?? null,
      alertas_abertos: alertas,
      desvios_abertos: desvios,
      has_valid_coordinates: valid,
    };
  });

  // ── Montar clusters ──
  const clusterPoints: GeoClusterPoint[] = (clustersRaw || []).map(c => {
    const { coords, valid } = resolveClusterCoords({ id: c.id, sede_municipio_ibge: c.sede_municipio_ibge });
    const myContratos = contratos.filter(ec => ec.cluster_id === c.id);
    const totalAlertas = myContratos.reduce((s, ec) => s + ec.alertas_abertos, 0);
    const totalDesvios = myContratos.reduce((s, ec) => s + ec.desvios_abertos, 0);
    const crit = myContratos.length > 0
      ? worstCriticidade(myContratos.map(ec => ec.criticidade))
      : 'sem_dados';

    return {
      id: c.id,
      name: c.name,
      color: c.color,
      coordinates: coords,
      criticidade: crit,
      contratos: myContratos,
      total_alertas: totalAlertas,
      total_desvios: totalDesvios,
      has_valid_coordinates: valid,
    };
  });

  return { clusters: clusterPoints, contratos };
}

// ── Hook ──

export const GEO_INTEL_QUERY_KEY = ['geo-intel-data'] as const;

const REALTIME_DEBOUNCE_MS = 3000;

export function useGeoIntelData() {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: GEO_INTEL_QUERY_KEY,
    queryFn: fetchGeoIntel,
    staleTime: 5 * 60 * 1000,
  });

  // Invalidação debounced — agrupa múltiplos eventos Realtime consecutivos (ex: lotes de geometria)
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });
    }, REALTIME_DEBOUNCE_MS);
  }, [queryClient]);

  // Listener Realtime — atualiza mapa GeoIntel ≤3s após qualquer alteração em clusters ou emp_contratos
  useEffect(() => {
    const channel = supabase
      .channel('geo-intel-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clusters' }, () => debouncedInvalidate())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'emp_contratos' }, () => debouncedInvalidate())
      .subscribe();

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [debouncedInvalidate]);

  return {
    clusters: data?.clusters ?? [],
    contratos: data?.contratos ?? [],
    loading: isLoading,
    refetch,
  };
}
